/**
 * IdeaImpactPanel
 *
 * Post-publish results for an idea, split organic vs paid. Rendered
 * below the Posts stage on the idea workspace — impact is a measured
 * outcome, not a pipeline step, so it stays out of the 3-stage rail.
 *
 * Numbers come pre-aggregated from ``GET /api/ideas/:id/impact``
 * (cumulative totals across the idea's published posts). Organic is
 * derived as ``total − paid`` server-side and labeled honestly.
 */

import React from "react";
import {
  ChartBarIcon,
  SpinnerIcon,
  InstagramIcon,
  FacebookIcon,
  TikTokIcon,
} from "@/components/icons";
import type { IconProps } from "@/components/icons";
import { useIdeaImpactQuery } from "../hooks/useIdeaImpact";
import type { ImpactBlock } from "../services/ideaImpact";
import {
  buildMetricRows,
  formatCount,
  formatSpend,
  formatShare,
  organicShare,
} from "../ideaImpactView";

const PLATFORM_ICONS: Record<string, React.FC<IconProps>> = {
  INSTAGRAM: InstagramIcon,
  FACEBOOK: FacebookIcon,
  TIKTOK: TikTokIcon,
};

const PLATFORM_LABELS: Record<string, string> = {
  INSTAGRAM: "Instagram",
  FACEBOOK: "Facebook",
  TIKTOK: "TikTok",
  ALL: "All platforms",
};

/** A single metric card: big total, organic/paid split bar + legend. */
const MetricCard: React.FC<{
  label: string;
  triple: { total: number | null; paid: number | null; organic: number | null };
  hasPaidSplit: boolean;
}> = ({ label, triple, hasPaidSplit }) => {
  const share = organicShare(triple);
  const organicPct = share === null ? 100 : Math.round(share * 100);

  return (
    <div className="bg-gray-50 rounded-lg p-3.5">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">
        {formatCount(triple.total)}
      </p>
      {hasPaidSplit ? (
        <>
          <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden bg-gray-200 flex">
            <div className="bg-blue-500 h-full" style={{ width: `${organicPct}%` }} />
            <div className="bg-purple-400 h-full" style={{ width: `${100 - organicPct}%` }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs">
            <span className="text-gray-600">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1 align-middle" />
              Organic {formatCount(triple.organic)}
            </span>
            <span className="text-gray-600">
              <span className="inline-block w-2 h-2 rounded-full bg-purple-400 mr-1 align-middle" />
              Paid {formatCount(triple.paid)}
            </span>
          </div>
          {share !== null && (
            <p className="text-[11px] text-gray-400 mt-1">{formatShare(share)} organic</p>
          )}
        </>
      ) : (
        <p className="text-[11px] text-gray-400 mt-1">Total only — no paid split</p>
      )}
    </div>
  );
};

const BlockCard: React.FC<{ block: ImpactBlock; emphasize?: boolean }> = ({
  block,
  emphasize,
}) => {
  const rows = buildMetricRows(block);
  const Icon = PLATFORM_ICONS[block.platform] ?? ChartBarIcon;
  const label = PLATFORM_LABELS[block.platform] ?? block.platform;
  const paid = block.paid;

  return (
    <div
      className={`rounded-xl border p-4 ${
        emphasize ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50/40"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        {rows.map((row) => (
          <MetricCard
            key={row.key}
            label={row.label}
            triple={row.triple}
            hasPaidSplit={row.hasPaidSplit}
          />
        ))}
      </div>
      {(paid.spend !== null || paid.results !== null) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600">
          <span>
            <span className="text-gray-400">Paid spend</span>{" "}
            <span className="font-semibold text-gray-900">{formatSpend(paid.spend)}</span>
          </span>
          <span>
            <span className="text-gray-400">Paid results</span>{" "}
            <span className="font-semibold text-gray-900">{formatCount(paid.results)}</span>
          </span>
          {paid.clicks !== null && (
            <span>
              <span className="text-gray-400">Paid clicks</span>{" "}
              <span className="font-semibold text-gray-900">{formatCount(paid.clicks)}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const IdeaImpactPanel: React.FC<{ ideaId: string | undefined }> = ({ ideaId }) => {
  const { data, isLoading, isError } = useIdeaImpactQuery(ideaId);

  // No heading of its own — the WorkspacePanel header already reads
  // "Impact · organic vs paid"; repeating it inside the body doubled it.

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <SpinnerIcon className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Could not load impact right now.
      </p>
    );
  }

  if (!data || !data.hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <ChartBarIcon className="w-9 h-9 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Impact appears once posts are published.</p>
        <p className="text-xs text-gray-400 mt-1">
          Organic and paid reach show here after this idea&apos;s posts go live.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-400">
        Cumulative totals across {data.postsWithData} published post
        {data.postsWithData === 1 ? "" : "s"}. Organic = total − paid.
      </p>
      {data.combined && <BlockCard block={data.combined} emphasize />}
      {data.platforms.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {data.platforms.map((block) => (
            <BlockCard key={block.platform} block={block} />
          ))}
        </div>
      )}
    </div>
  );
};
